import { type UseQueryOptions } from '@tanstack/react-query'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'
import useQuery from '~/hooks/utils/useQuery'

import type { FileMetadata } from './useFilesQuery'

export interface RecentFilesResponse {
  files: FileMetadata[]
  totalCount: number
}

// Query key generator function
export const getRecentFilesQueryKeys = (accountId?: string, limit?: number) => {
  const base = ['recentFiles'] as const
  if (!accountId) return base
  return [...base, accountId, limit] as const
}

// Fetch function
export const fetchRecentFiles = async (accountId: string, limit: number = 10, signal?: AbortSignal): Promise<RecentFilesResponse> => {
  if (!accountId) throw new Error('Account ID is missing')

  const response = await api.get(`/provide/${accountId}/file/recent`, {
    params: { limit },
    signal
  })

  if (!response.data) throw new Error('No files found')

  return response.data as RecentFilesResponse
}

// Query hook
export const useRecentFilesQuery = (limit?: number, options?: UseQueryOptions<RecentFilesResponse>) => {
  const account = useAccount()

  return useQuery(getRecentFilesQueryKeys(account.id, limit), ({ signal }) => fetchRecentFiles(account.id, limit, signal), {
    enabled: options?.enabled === undefined ? !!account.id : !!account.id && options.enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options
  })
}

export default useRecentFilesQuery
